import config from '../../config';
import API from '../api';

const { baseUrl } = config.coreApi;

//#region Types
/**
 * @typedef DeviceComment
 * @type {Object}
 * @property {string} createTime - Creation time of the comment.
 * @property {string} id - ID of the comment.
 * @property {string} text - Text of the comment.
 */

/**
 * @typedef DeviceState
 * @type {Object}
 * @property {string} admproviderVersion - ADM provider version.
 * @property {string} malosVersion - Malos version.
 * @property {boolean} managed - Indicates if the device is managed.
 * @property {string} platform - Platform of the device.
 * @property {string} status - Status of the device.
 * @property {string} updateTime - Last update time of the device state.
 */

/**
 * @typedef DeviceConfiguration
 * @type {Object}
 * @property {string} companyId - Company ID associated with the configuration.
 * @property {Object} customConfig - Custom configuration for the device.
 * @property {Object} defaultConfig - Default configuration for the device.
 * @property {string} defaultSolutionConfigId - Default solution configuration ID.
 * @property {string} deviceId - Device ID associated with the configuration.
 * @property {string} name - Name of the configuration.
 * @property {Object} solutionConfig - Solution configuration for the device.
 */

/**
 * @typedef Device
 * @type {Object}
 * @property {boolean} archived - Indicates if the device is archived.
 * @property {string[]} attachedSites - Array of attached sites.
 * @property {string} companyId - Company ID associated with the device.
 * @property {string} createTime - Creation time of the device.
 * @property {string} creatorId - Creator ID of the device.
 * @property {DeviceComment[]} deviceComments - Array of comments associated with the device.
 * @property {string} displayName - Display name of the device.
 * @property {boolean} enableAutoUpdate - Indicates if auto-update is enabled.
 * @property {boolean} hasWifi - Indicates if the device has WiFi.
 * @property {string} heatmapImageUrl - URL of the heatmap image.
 * @property {string} id - ID of the device.
 * @property {("INTEGRATION_NAME_UNSPECIFIED" | "BRIGHTAUTHORFACEV0" | "AYUDAFACEV1" | "BROADSIGNFACEV1" | "SIGNAGELIVEFACEV1" | "BRIGHTAUTHORFACEV1" | "BROADSIGNVEHICLEV1" | "SCALAFACEV0" | "HIVESTACKFACEV1"[])} integrations - Array of enabled integrations for the device.
 * @property {Object.<string, string>} labels - Labels associated with the device.
 * @property {string} name - Name of the device.
 * @property {string} projectId - Project ID associated with the device.
 * @property {string} provisioningToken - Provisioning token for the device.
 * @property {boolean} provisioningTokenWasUsed - Indicates if the provisioning token was used.
 * @property {"SOLUTION_NAME_UNSPECIFIED" | "VEHICLERECOGNITIONV1" | "FACEV2" | "VEHICLECROWD" | "CROWDV3" | "VEHICLEDETECTIONV1"} solution - Solution associated with the device.
 * @property {DeviceState} state - State of the device.
 * @property {string[]} tags - Tags associated with the device.
 * @property {string} updateTime - Last update time of the device.
 */

//#endregion

//#region CREATE
/**
 * Creates a new device.
 * @param {string} pid - Project ID.
 * @param {Device} device - Device to be created.
 * @returns {Device} Created device.
 */
const createDevice = async (pid, device) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices`;
  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(device)
  }).then(res => res.json());
};

/**
 * Creates a new device comment.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @param {string} text - Comment to be created.
 * @returns {Device} Created device.
 */
const createDeviceComment = async (pid, did, text) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}/comments`;
  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify({ comment: { text } })
  }).then(res => res.json());
};

/**
 * Archives a single device.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @returns {Device} Archived device.
 */
const archiveDevice = async (pid, did) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}:archive`;
  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() }
  }).then(res => res.json());
};

/**
 * Dearchives a single device.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @returns {Device} Dearchived device.
 */
const dearchiveDevice = async (pid, did) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}:dearchive`;
  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() }
  }).then(res => res.json());
};
//#endregion

//#region READ
/**
 * List existing devices.
 * @param {string} pid - Project ID.
 * @param {string} page - page token (first page is defined as empty string).
 * @param {string} filter - Filter expression.
 * @param {string} pageSize - Amount of objects returned.
 * @param {string} orderBy - Order expression.
 * @returns {Device[]} devicesgetDevices
 */
const getDevices = async (pid, page = '', filter = '', pageSize = 40, orderBy = 'displayName, id') => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices?pageSize=${pageSize}&pageToken=${page}&filter=${filter}&orderBy=${orderBy}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

const getDevicesByCompany = async (cid, page = '', filter = '', pageSize = 40, orderBy = 'displayName, id') => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/companies/${cid}/devices?pageSize=${pageSize}&pageToken=${page}&filter=${filter}&orderBy=${orderBy}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};
// The portal endpoint resolves devices through campaign sites and assets.
const getDevicesByBrandPortal = async bpid => {
  const devices = new Map();
  let pageToken = '';
  do {
    await API.refreshTokenWhenNeeded();
    const query = new URLSearchParams({ pageSize: '200', pageToken, orderBy: 'displayName, id' });
    const res = await fetch(`${baseUrl}/brandportals/${bpid}/devices?${query}`, {
      headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() }, method: 'GET'
    });
    const page = await API.generateResponse(res);
    (page.devices || []).forEach(device => devices.set(device.id, device));
    pageToken = page.nextPageToken || '';
  } while (pageToken);
  return Array.from(devices.values());
};
/**
 * Gets a single device.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @param {string} page - page token (first page is defined as empty string).
 * @param {string} filter - Filter expression.
 * @param {string} orderBy - Order expression.
 * @returns {Device} device
 */
const getDevice = async (pid, did) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Gets the device's config.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @param {string} customHeaders - custom API headers in case of impersonating user
 * @returns {DeviceConfiguration} device configuration.
 */
const getDeviceConfig = async (pid, did, customHeaders) => {
  const requestHeader = customHeaders ? customHeaders : API.headers;
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}/configuration`;
  return fetch(url, {
    headers: { ...requestHeader, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @param {('start'|'stop'|'restart'|'preview')} command - command sent to API.
 * @returns {{}}
 */
const sendCommand = async (pid, did, command) => {
  await API.refreshTokenWhenNeeded();

  const commandsMapping = {
    restart: 'restartDetection',
    start: 'startDetection',
    stop: 'stopDetection',
    preview: 'getPreviewImage'
  };

  const url = `${baseUrl}/projects/${pid}/devices/${did}:${commandsMapping[command]}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  })
    .then(res => API.generateResponse(res))
    .then(res => {
      if (res.status && (res.status === 'failed' || res.status === 'timeout')) {
        throw res.answer;
      }
      return res;
    });
};
//#endregion

//#region UPDATE
/**
 * Provisions a single device
 * @param {string} token - provisioning Token.
 * @returns {{credentials: {certificatePem: string, privateKey: string, publicKey: string}}} devices
 */
const provisionDevice = async token => {
  await API.refreshTokenWhenNeeded();
  const url = `/companies/-/devices:provision`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'PATCH',
    body: JSON.stringify({ provisioningToken: token })
  }).then(res => API.generateResponse(res));
};

/**
 * Updates a device.
 * @param {string} pid - Project ID.
 * @param {string} device - Device being updated.
 * @param {string[]} fields - Company fields being updated. Comma separated.
 * @returns {Device} Device updated.
 */
const updateDevice = async (pid, device, fields) => {
  await API.refreshTokenWhenNeeded();

  const paths = `updateMask=${fields.join(',')}`;
  const body = {};

  fields.forEach(field => {
    body[field] = device[field];
  });

  const url = `${baseUrl}/projects/${pid}/devices/${device.id}?${paths}`;

  return fetch(url, {
    method: 'PATCH',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(body)
  }).then(res => res.json());
};

/**
 * Updates the device's config.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @param {string} configutarion - Device configuration.
 * @param {string[]} fields - Company fields being updated. Comma separated.
 * @returns {Device} Device updated.
 */
const updateDeviceConfig = async (pid, did, configuration, fields) => {
  await API.refreshTokenWhenNeeded();

  const paths = `updateMask=${fields.join(',')}`;
  const body = {};

  fields.forEach(field => {
    body[field] = configuration[field];
  });

  const url = `${baseUrl}/projects/${pid}/devices/${did}/configuration?${paths}`;

  return fetch(url, {
    method: 'PATCH',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(body)
  }).then(res => res.json());
};

/**
 * Enables or disables heatmap generation for a device.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @param {('enable'|'disable')} action - Action to be performed.
 * @returns {{}}
 */
const toggleHeatmap = async (pid, did, action = 'enable') => {
  await API.refreshTokenWhenNeeded();

  const toggleString = action === 'enable' ? 'enable' : 'disable';
  const url = `${baseUrl}/projects/${pid}/devices/${did}:${toggleString}Heatmap`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'PATCH'
  }).then(res => API.generateResponse(res));
};
//#endregion

//#region DELETE
/**
 * Deletes a single device.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @returns {{}}
 */
const deleteDevice = async (pid, did) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'DELETE'
  }).then(res => API.generateResponse(res));
};

const setHeatmapImage = async (pid, did, imageUrl) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}:setHeatmapImage`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'PATCH',
    body: JSON.stringify({ imageUrl })
  }).then(res => API.generateResponse(res));
};

/**
 * Deletes a device comment.
 * @param {string} pid - Project ID.
 * @param {string} did - Device ID.
 * @param {string} cmid - Comment ID.
 * @returns {{}}
 */
const deleteDeviceComment = async (pid, did, cmid) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/devices/${did}/comments/${cmid}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'DELETE'
  }).then(res => API.generateResponse(res));
};
//#endregion

export {
  createDevice,
  createDeviceComment,
  archiveDevice,
  dearchiveDevice,
  getDevices,
  getDevice,
  getDeviceConfig,
  sendCommand,
  provisionDevice,
  updateDevice,
  updateDeviceConfig,
  toggleHeatmap,
  deleteDevice,
  deleteDeviceComment,
  setHeatmapImage,
  getDevicesByCompany,
  getDevicesByBrandPortal
};
